/**
 * Bibliothèque de fonctions mathématiques pour le générateur paramétrique
 * d'exercices ("Blueprint"). Catalogue fermé, codé et testé à la main —
 * volontairement PAS de génération de code par IA ni de sandbox d'exécution
 * (cf. décision de cadrage du plan : une fonction manquante est signalée pour
 * développement humain plutôt qu'exécutée depuis du code généré).
 *
 * Chaque fonction est pure, à arité fixe, et ne prend/renvoie que des nombres.
 * Le Blueprint choisit dans ce catalogue via son id ; l'étape LLM
 * `blueprint_extract` ne voit qu'un miroir léger (métadonnées, sans
 * implémentation) — cf. supabase/functions/_shared/blueprint-math-catalog.ts.
 */

export interface MathFunctionSignature {
  /** Noms des paramètres, dans l'ordre d'appel. */
  params: readonly string[];
}

export interface MathFunctionDef {
  id: string;
  label: string;
  signature: MathFunctionSignature;
  fn: (...args: number[]) => number;
}

function def(
  id: string,
  label: string,
  params: readonly string[],
  fn: (...args: number[]) => number,
): MathFunctionDef {
  return { id, label, signature: { params }, fn };
}

export const MATH_FUNCTIONS: Readonly<Record<string, MathFunctionDef>> = Object.freeze({
  rectangleArea: def(
    'rectangleArea',
    "Aire d'un rectangle",
    ['longueur', 'largeur'],
    (l, w) => l * w,
  ),
  rectanglePerimeter: def(
    'rectanglePerimeter',
    "Périmètre d'un rectangle",
    ['longueur', 'largeur'],
    (l, w) => 2 * (l + w),
  ),
  squareArea: def('squareArea', "Aire d'un carré", ['cote'], (c) => c * c),
  squarePerimeter: def('squarePerimeter', "Périmètre d'un carré", ['cote'], (c) => 4 * c),
  circleArea: def('circleArea', "Aire d'un disque", ['rayon'], (r) => Math.PI * r * r),
  circlePerimeter: def(
    'circlePerimeter',
    "Périmètre d'un cercle",
    ['rayon'],
    (r) => 2 * Math.PI * r,
  ),
  triangleArea: def(
    'triangleArea',
    "Aire d'un triangle",
    ['base', 'hauteur'],
    (b, h) => (b * h) / 2,
  ),
  pythagoras: def(
    'pythagoras',
    'Hypoténuse (théorème de Pythagore)',
    ['cote_a', 'cote_b'],
    (a, b) => Math.sqrt(a * a + b * b),
  ),
  pythagoreanLeg: def(
    'pythagoreanLeg',
    'Côté manquant (théorème de Pythagore)',
    ['hypotenuse', 'autre_cote'],
    (hyp, other) => Math.sqrt(Math.max(hyp * hyp - other * other, 0)),
  ),
  percentageOf: def(
    'percentageOf',
    "Pourcentage d'une valeur",
    ['valeur', 'pourcentage'],
    (v, p) => (v * p) / 100,
  ),
  percentageChange: def(
    'percentageChange',
    'Variation en pourcentage entre deux valeurs',
    ['valeur_initiale', 'valeur_finale'],
    (initial, final) => ((final - initial) / initial) * 100,
  ),
  mean2: def('mean2', 'Moyenne de deux valeurs', ['a', 'b'], (a, b) => (a + b) / 2),
  mean3: def('mean3', 'Moyenne de trois valeurs', ['a', 'b', 'c'], (a, b, c) => (a + b + c) / 3),
  discriminant: def(
    'discriminant',
    "Discriminant d'un trinôme du second degré",
    ['a', 'b', 'c'],
    (a, b, c) => b * b - 4 * a * c,
  ),
  gcd: def('gcd', 'PGCD de deux entiers', ['a', 'b'], (a, b) => {
    let x = Math.abs(Math.round(a));
    let y = Math.abs(Math.round(b));
    while (y !== 0) [x, y] = [y, x % y];
    return x;
  }),
  lcm: def('lcm', 'PPCM de deux entiers', ['a', 'b'], (a, b) => {
    const x = Math.abs(Math.round(a));
    const y = Math.abs(Math.round(b));
    if (x === 0 || y === 0) return 0;
    let gx = x;
    let gy = y;
    while (gy !== 0) [gx, gy] = [gy, gx % gy];
    return (x / gx) * y;
  }),
  product: def(
    'product',
    'Produit de deux valeurs (ex. prix × quantité)',
    ['a', 'b'],
    (a, b) => a * b,
  ),
  distance2D: def(
    'distance2D',
    'Distance entre deux points du plan',
    ['x1', 'y1', 'x2', 'y2'],
    (x1, y1, x2, y2) => Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2),
  ),
  slope: def(
    'slope',
    'Coefficient directeur entre deux points',
    ['x1', 'y1', 'x2', 'y2'],
    (x1, y1, x2, y2) => (y2 - y1) / (x2 - x1),
  ),
  speed: def('speed', 'Vitesse moyenne (distance / temps)', ['distance', 'temps'], (d, t) => d / t),
  parabolaVertexCoefficient: def(
    'parabolaVertexCoefficient',
    "Coefficient directeur a d'une parabole sous forme canonique f(x)=a(x-h)²+k, à partir du sommet (h,k) et d'un autre point (x1,y1) de la courbe",
    ['x1', 'y1', 'h', 'k'],
    (x1, y1, h, k) => (y1 - k) / ((x1 - h) * (x1 - h)),
  ),
  roundTo: def('roundTo', 'Arrondi à N décimales', ['valeur', 'decimales'], (v, n) => {
    const scale = 10 ** Math.max(0, Math.round(n));
    return Math.round(v * scale) / scale;
  }),
});

export const MATH_FUNCTION_IDS: readonly string[] = Object.freeze(Object.keys(MATH_FUNCTIONS));

/** Appelle une fonction du catalogue par id, avec vérification d'arité. Lève si id/arité invalide. */
export function callMathFunction(id: string, args: readonly number[]): number {
  const def_ = MATH_FUNCTIONS[id];
  if (!def_) throw new Error(`Fonction mathématique inconnue : ${id}`);
  if (args.length !== def_.signature.params.length) {
    throw new Error(
      `Arité invalide pour ${id} : attendu ${def_.signature.params.length}, reçu ${args.length}`,
    );
  }
  return def_.fn(...args);
}
